import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/Header';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

const UserProfile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    department: ''
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setFormData({
        name: userData?.name || '',
        email: userData?.email || '',
        phone: userData?.phone || '',
        position: userData?.position || 'Gestor Financeiro',
        department: userData?.department || 'Financeiro'
      });
    } else {
      const defaultUser = {
        name: 'Rita Silva',
        email: 'rita@jrwise.com.br',
        phone: '+55 11 98765-4321',
        position: 'Gestor Financeiro',
        department: 'Financeiro',
        role: 'JR WISE TOUR',
        initials: 'RS'
      };
      setUser(defaultUser);
      setFormData({
        name: defaultUser?.name,
        email: defaultUser?.email,
        phone: defaultUser?.phone,
        position: defaultUser?.position,
        department: defaultUser?.department
      });
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    const updatedUser = {
      ...user,
      ...formData,
      initials: formData?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase() || 'RS'
    };
    
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    setUser(updatedUser);
    setIsEditing(false);
    
    alert('Perfil atualizado com sucesso!');
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      position: user?.position || '',
      department: user?.department || ''
    });
    setIsEditing(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Voltar</span>
          </button>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8">
              <div className="flex items-center gap-6">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white shadow-lg ring-4 ring-white/50">
                  <span className="text-3xl font-bold text-blue-600">{user?.initials}</span>
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-white mb-2">{user?.name}</h1>
                  <p className="text-blue-100 text-lg">{user?.role}</p>
                </div>
                {!isEditing && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="bg-white text-blue-600 hover:bg-blue-50 border-0"
                  >
                    <Icon name="Edit" size={18} className="mr-2" />
                    Editar Perfil
                  </Button>
                )}
              </div>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome Completo
                  </label>
                  {isEditing ? (
                    <Input
                      value={formData?.name}
                      onChange={(e) => handleInputChange('name', e?.target?.value)}
                      placeholder="Digite seu nome completo"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                      <Icon name="User" size={18} className="text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{user?.name}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    E-mail
                  </label>
                  {isEditing ? (
                    <Input
                      type="email"
                      value={formData?.email}
                      onChange={(e) => handleInputChange('email', e?.target?.value)}
                      placeholder="Digite seu e-mail"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                      <Icon name="Mail" size={18} className="text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{user?.email}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Telefone
                  </label>
                  {isEditing ? (
                    <Input
                      value={formData?.phone}
                      onChange={(e) => handleInputChange('phone', e?.target?.value)}
                      placeholder="Digite seu telefone"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                      <Icon name="Phone" size={18} className="text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{user?.phone || 'Não informado'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cargo
                  </label>
                  {isEditing ? (
                    <Input
                      value={formData?.position}
                      onChange={(e) => handleInputChange('position', e?.target?.value)}
                      placeholder="Digite seu cargo"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                      <Icon name="Briefcase" size={18} className="text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{user?.position || 'Não informado'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Departamento
                  </label>
                  {isEditing ? (
                    <Input
                      value={formData?.department}
                      onChange={(e) => handleInputChange('department', e?.target?.value)}
                      placeholder="Digite seu departamento"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                      <Icon name="Building" size={18} className="text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{user?.department || 'Não informado'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Função
                  </label>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                    <Icon name="Shield" size={18} className="text-gray-400" />
                    <span className="text-gray-900 dark:text-white">{user?.role}</span>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
                  <Button
                    onClick={handleSave}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Icon name="Save" size={18} className="mr-2" />
                    Salvar Alterações
                  </Button>
                  <Button
                    onClick={handleCancel}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white"
                  >
                    <Icon name="X" size={18} className="mr-2" />
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="Lock" size={20} className="text-blue-600" />
              Segurança
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Gerencie suas configurações de segurança e privacidade
            </p>
            <Button className="bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white">
              <Icon name="Key" size={18} className="mr-2" />
              Alterar Senha
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;